import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectShareLink } from "../../../selectors/selectShareLink.selector";
import type { AppState } from "../../../state/root.reducer";
import { selectProjectById } from "../../../state/workspaces/projects.selector";
import { updateWorkspaceGlobalAccess } from "../../../state/workspaces/workspaces.actions";
import {
  selectIsUpdatingWorkspace,
  selectPublicPermissionLevel,
  selectSelectedWorkspace,
} from "../../../state/workspaces/workspaces.selector";
import { emailRegex } from "../../../utils/emailRegex";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { getGlobalAccessFromShareOption } from "../../../utils/sharing.helpers";
import { type Contact, makeContact } from "../../RecipientInput";
import { CopyEmailLink } from "./CopyEmailLink";
import { LockIcon } from "./icons/LockIcon";
import { UsersIcon } from "./icons/UsersIcon";
import type { PermissionOptionProps } from "./PermissionOption";
import { PermissionsBar } from "./PermissionsBar";
import Styles from "./PermissionsSection.module.css";
import { PublicSharing } from "./PublicSharingSection";
import { ShareSection } from "./ShareSection";
import { ShareSectionHeader } from "./ShareSectionHeader";
import { ShareType } from "./ShareType";
import { WorkspacePassword } from "./WorkspacePassword";
import { WorkspacePermissions } from "./WorkspacePermissions";

const options: ShareOptionValue[] = [
  ShareType.EDIT,
  ShareType.READ,
  ShareType.NO_ACCESS,
];

export type PermissionsSectionProperties = {
  emailPreset: { emails: string };
  handleWorkspacePassword: (
    password: string,
    currentPassword: string,
    mode: string,
  ) => void;
  initiallyOpen: boolean;
  isAdmin: boolean;
  sectionTitle: string;
};

export const PermissionsSection: React.FC<PermissionsSectionProperties> = ({
  emailPreset,
  handleWorkspacePassword,
  initiallyOpen,
  isAdmin,
  sectionTitle,
}) => {
  const dispatch = useDispatch();

  const tShareType = useI18n("SHARE_TYPE.");
  const tShareTypeLink = useI18n("SHARE_TYPE.LINK.");
  const tShareWorkspaceDialog = useI18n("SHARE_WORKSPACE_DIALOG.");

  const selectedMyWorkspaceMetadata = useSelector(selectSelectedWorkspace);
  const project = useSelector((state: AppState) =>
    selectProjectById(
      state,
      selectedMyWorkspaceMetadata?.containerId ?? undefined,
    ),
  );
  const shareLink = useSelector((state: AppState) =>
    selectShareLink(state, selectedMyWorkspaceMetadata?.workspaceId),
  );
  const publicPermissionLevel = useSelector(selectPublicPermissionLevel);
  const isUpdatingWorkspace = useSelector(selectIsUpdatingWorkspace);
  const requiresPassword = useSelector(
    (state: AppState) => state.context.user.requiresPassword,
  );

  const [isSectionVisible, setIsSectionVisible] = useState(
    !!emailPreset || initiallyOpen,
  );
  const [isPasswordSectionActive, setIsPasswordSectionActive] = useState(false);
  const [isUsersSectionActive, setIsUsersSectionActive] = useState(
    !!emailPreset || initiallyOpen,
  );
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);

  useEffect(() => {
    const newContactsFromProps = splitContacts(emailPreset.emails).reduce(
      (acc: Contact[], contact: Contact) => {
        const alreadyAdded = selectedContacts.find(
          (selected) => selected.email === contact.email,
        );

        if (!alreadyAdded) {
          acc.push(contact);
        }

        return acc;
      },
      [],
    );

    setSelectedContacts((selectedContacts) => [
      ...selectedContacts,
      ...newContactsFromProps,
    ]);
  }, [emailPreset]);

  const hasPassword = !!selectedMyWorkspaceMetadata?.hasPassword;

  const openPasswordSection = (isActive: boolean) => {
    setIsSectionVisible(isActive);
    setIsPasswordSectionActive(isActive);
    setIsUsersSectionActive(false);
  };
  const openUsersSection = (isActive: boolean) => {
    setIsSectionVisible(isActive);
    setIsUsersSectionActive(isActive);
    setIsPasswordSectionActive(false);
  };
  const toggleOnHeaderClick = () => {
    setIsSectionVisible(!isSectionVisible);
    if (isSectionVisible) {
      setIsPasswordSectionActive(false);
      setIsUsersSectionActive(false);
    } else {
      setIsUsersSectionActive(true);
    }
  };

  const commitUpdate = useCallback(
    (shareOption: ShareOptionValue) => {
      if (!selectedMyWorkspaceMetadata?.workspaceId) {
        return;
      }
      dispatch(
        updateWorkspaceGlobalAccess.request({
          globalAccess: getGlobalAccessFromShareOption(shareOption),
          workspaceId: selectedMyWorkspaceMetadata.workspaceId,
        }),
      );
    },
    [selectedMyWorkspaceMetadata],
  );

  const linkOptions = useMemo(
    () =>
      options.map<PermissionOptionProps>((option) => ({
        ...option,
        onClick: () => commitUpdate(option),
        selected: option.value === publicPermissionLevel.value,
        title: tShareType(option.title),
        tooltip: option.tooltip && tShareTypeLink(option.tooltip),
      })),
    [commitUpdate, publicPermissionLevel, tShareType, tShareTypeLink],
  );

  const permissionIcons: React.JSX.Element[] = [
    <LockIcon
      hasPassword={hasPassword}
      isActive={isPasswordSectionActive}
      isAdmin={isAdmin}
      key="permissions-lock"
      toggleActive={openPasswordSection}
    />,
    <UsersIcon
      isActive={isUsersSectionActive}
      key="permissions-users"
      toggleActive={openUsersSection}
    />,
  ];

  /**
   * Determines whether to show the new public sharing UI or the old link sharing UI.
   * - No containerId: workspace is outside a project → old UI
   * - autoSubscribe === false: auto-subscribe explicitly disabled on project → old UI
   * - autoSubscribe === true or unset (null): workspace assigned to a project → public sharing UI
   * - project data unavailable: !==false treats undefined as true → public sharing UI (fallback)
   */
  const showPublicSharing =
    !!selectedMyWorkspaceMetadata?.containerId &&
    project?.autoSubscribe !== false;

  return (
    <ShareSection testId="set-permissions">
      <ShareSectionHeader
        alwaysShowIcons
        icons={permissionIcons}
        isSectionVisible={isSectionVisible}
        onHeaderClick={toggleOnHeaderClick}
        sectionIcon="hoylu-ui-icons-protected-access"
        sectionTitle={sectionTitle}
      />
      {isSectionVisible && (
        <div className={Styles.permissionsSectionModule}>
          {isUsersSectionActive && (
            <>
              {selectedMyWorkspaceMetadata && (
                <CopyEmailLink
                  shareLink={shareLink}
                  workspaceName={
                    selectedMyWorkspaceMetadata.workspaceName ?? ""
                  }
                />
              )}
              {!showPublicSharing && (
                <PermissionsBar
                  isLoading={isUpdatingWorkspace}
                  label={tShareWorkspaceDialog("ANYONE_WITH_LINK")}
                  options={linkOptions}
                />
              )}
              <WorkspacePermissions
                isAddingUsersFromExternalSource={!!emailPreset.emails}
                selectedContacts={selectedContacts}
                setSelectedContacts={setSelectedContacts}
              />
              {isAdmin && showPublicSharing && (
                <PublicSharing updateGlobalAccess={commitUpdate} />
              )}
            </>
          )}
          {isPasswordSectionActive && (
            <WorkspacePassword
              confirmPasswordAction={(password, currentPassword, mode) =>
                handleWorkspacePassword(password, currentPassword, mode)
              }
              hasPassword={hasPassword}
              requiresPassword={requiresPassword}
            />
          )}
        </div>
      )}
    </ShareSection>
  );
};

function splitContacts(text: string) {
  return text
    .split(/[,; ]/)
    .filter((e) => e.match(emailRegex))
    .map(makeContact);
}
